<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, onMounted, getCurrentInstance, computed, watch } from 'vue'
import _ from 'lodash'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { useNotification } from "@kyvg/vue3-notification"
import { clipboardCopy } from '../../utils'
import UserService from "@/services/UserService"
import DemoVideoModal from '@/components/modals/DemoVideoModal.vue'
import IconBase from '../../components/icon/IconBase.vue'
import IconShowcase from '../../components/icon/IconShowcase.vue'
import IconBookOpen from '../../components/icon/IconBookOpen.vue'
import FormButton from '../../components/Atoms/FormButton.vue'
import TabGroup from '../../components/Atoms/TabGroup.vue'
import TabItem from '../../components/Atoms/TabItem.vue'
import ShowcasePreviewModal from '../../components/modals/ShowcasePreviewModal.vue'
import IconLink from '../../components/icon/IconLink.vue'
import IconLinkExternal from '../../components/icon/IconLinkExternal.vue'

const route = useRoute()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

// Refs
const preview = ref(false)
const showcase = ref(null)
const sortedStream = ref(null)
const publishesStatus = ref(false)
const publicshowcase = ref(null)

// Hooks
onMounted(async () => {
  await checkStatus()
})

// Computed
const showcaseStorage = computed(() => store.getters['Showcase/showcase'])
const showcaseStoragePublic = computed(() => store.getters['Showcase/showcasePublic'])
const shouldPublish = computed(() => store.getters['Showcase/shouldPublish'])

// Watch 
watch(showcaseStorage, (newValue, oldValue) => {
  if (oldValue !== null) {
    publishesStatus.value = true
  }
})

// Methods
const checkStatus = async () => {
  try {
    await store.dispatch('Showcase/getShowcase')
    showcase.value = _.cloneDeep(showcaseStorage.value)
    await store.dispatch('Showcase/getPublicShowcase', showcase.value._id)

  }
  catch (e) {
    console.log(e)
    publishesStatus.value = false
  }
}

const openPreview = async () => {
  showcase.value = await UserService.getShowcase()
  preview.value = true
  sortStream(showcase.value)
}

const sortStream = (sobject) => {
  const sorted = sobject.streams.concat(sobject.vods)
  sorted.map(s => {
    if (s.id) {
      s._id = s.id
    }
  })
  sorted.sort((a, b) => {
    if (sobject.streamOrder.indexOf(a._id) > sobject.streamOrder.indexOf(b._id)) {
      return 1
    } else {
      return -1
    }
  })
  sortedStream.value = sorted
}

const publish = async (events = false) => {
  const publish = await UserService.publishShowcase(showcase.value._id)
  showcase.value = publish
  if (publish.streams) {
    await store.dispatch('Showcase/getPublicShowcase', showcase.value._id)
    notify({ group: "success", text: 'Showcase content published' })
  }
  publishesStatus.value = false
}

const copyLink = () => {
  const link = `https://castr.com/showcase/${showcase.value._id}`
  clipboardCopy(link)
}

const arraysEqual = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return false
  if (a.length !== b.length) return false
  for (var i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) return false
  }
  return true
}

const showDemoModal = () => showModal('modal-demo-video')

</script>

<template>
  <div class="container detail-shell px-4">
    <header class="mt-6 mb-4 flex flex-wrap gap-2">
      <div>
        <h1 class="page-title mb-0">Showcase</h1>
        <p class="flex flex-wrap w-full items-center gap-4">
          <FormButton type="link" target="_blank"
            class="flex gap-x-2 items-center !text-surface-6  hover:!text-surface-9 hover:no-underline"
            href="https://docs.castr.com/en/articles/7220837-set-up-a-showcase">
            <icon-base class="">
              <icon-book-open />
            </icon-base>
            How Showcase works
          </FormButton>
          <FormButton type="link"
            class="flex items-center gap-x-2 !text-surface-6 hover:!text-surface-9 hover:no-underline"
            @click="showDemoModal">
            <icon-base class="">
              <IconShowcase />
            </icon-base>
            Showcase Demo
          </FormButton>
        </p>
      </div>
      <div class="flex gap-x-2 items-center lg:justify-end lg:ml-auto">
        <FormButton type="secondary" class="gap-x-2" size="md" @click="copyLink"
          v-if="showcaseStoragePublic && ((showcaseStoragePublic.streams.length != 0) || (showcaseStoragePublic.vods.length != 0))">
          <icon-base>
            <IconLink />
          </icon-base>
          Copy Link
        </FormButton>
        <FormButton type="secondary" class="gap-x-2" size="md" @click="openPreview"
          v-if="showcase && ((showcase.streams.length != 0) || (showcase.vods.length != 0))">
          <icon-base>
            <IconLinkExternal />
          </icon-base>
          Open Preview
        </FormButton>
        <FormButton size="md" type="main" @click="publish" :disabled="!shouldPublish">
          Publish
        </FormButton>
      </div>
    </header>
    <section>
      <TabGroup type="lines" class="w-full">
        <TabItem type="line" label="Content" :isActive="route.path === '/showcase/content'" to="/showcase/content" />
        <TabItem type="line" label="Settings" :isActive="route.path === '/showcase/settings'" to="/showcase/settings" />
      </TabGroup>
    </section>
    <router-view></router-view>
  </div>
  <ShowcasePreviewModal :showcase="showcase" :publish="publish" :sortedStream="sortedStream"
    v-model:is-visible="preview" :publishes-status="publishesStatus" />
  <demo-video-modal videoid="hWjuTQYhYQ2UKlgVkwa7s" />
</template>

<style scoped></style>
